/**
 * Role/status badge styling for the Platform Members roster (DNKADM-0015).
 *
 * These are the saas-auth **login** role/status values — a distinct axis from the
 * operational membership `STATUS`/`TIER_CHIP` tokens (which describe GHL booking
 * contacts), so they get their own small color maps here rather than reusing those.
 *
 * The known role set is `OWNER | SUPER_ADMIN | ADMIN | STAFF | USER | GUEST` (STAFF added by
 * DNKADM-0360; SUPER_ADMIN is display-only now — it is no longer assignable, but it still
 * arrives on existing rows and support-session audit trails) and the known status set is
 * `ACTIVE | INVITED | DISABLED`. Known enums get a themed fg/bg pair (and a short
 * meaning where the spec calls for one — `SUPER_ADMIN` = "elevated administrator",
 * `INVITED` = "pending — hasn't joined", `DISABLED` = "revoked"); an UNKNOWN future
 * enum falls back to a neutral chip whose label is the RAW wire string, so a value
 * saas-auth adds later still renders without crashing.
 *
 * MODE-AWARE since DNKADM-0269: the ink/tint pairs live in
 * `src/theme/maps/platformMembers.ts` (this feature dir is literal-free under the
 * 0269 ESLint guard). Callers pass `theme.palette.mode`; the default keeps light —
 * and every pre-0269 call site/test — byte-identical.
 */
import { buildPlatformMemberBadgeMaps } from '@/theme/maps/platformMembers';

export interface BadgeStyle {
  /** Display label — the known label, or the raw wire string for an unknown enum. */
  label: string;
  fg: string;
  bg: string;
  /** Short meaning shown as a tooltip / secondary line, when the enum carries one. */
  hint?: string;
  /** True when the value is outside the known union (rendered raw, neutral). */
  unknown: boolean;
}

/** Resolve the badge style for a platform role; an unknown value renders raw/neutral. */
export function roleBadge(role: string, mode: 'light' | 'dark' = 'light'): BadgeStyle {
  const maps = buildPlatformMemberBadgeMaps(mode);
  const known = (maps.role as Record<string, PlatformBadgePairShape>)[role];
  if (known) return { ...known, unknown: false };
  return { label: role, ...maps.neutral, unknown: true };
}

/** Resolve the badge style for a platform status; an unknown value renders raw/neutral. */
export function statusBadge(status: string, mode: 'light' | 'dark' = 'light'): BadgeStyle {
  const maps = buildPlatformMemberBadgeMaps(mode);
  const known = (maps.status as Record<string, PlatformBadgePairShape>)[status];
  if (known) return { ...known, unknown: false };
  return { label: status, ...maps.neutral, unknown: true };
}

type PlatformBadgePairShape = { label: string; fg: string; bg: string; hint?: string };
